// PaymentModal.js - Modal för att registrera betalning

import React, { useState, useEffect } from 'react';
import { registerPayment, ensureRealAppointment } from './calendarApi';
import './PaymentModal.css';

const PAYMENT_METHODS = [
  { value: 'card', label: 'Kort (Nets)', icon: 'fa-credit-card' },
  { value: 'cash', label: 'Kontant', icon: 'fa-money-bill-wave' },
  { value: 'swish', label: 'Swish', icon: 'fa-mobile-alt' },
  { value: 'frikort', label: 'Frikort', icon: 'fa-id-card' },
  { value: 'uteblivet', label: 'Uteblivet', icon: 'fa-user-slash' },
  { value: 'avgiftsfri', label: 'Avgiftsfritt (ej fysiskt besök)', icon: 'fa-phone' }
];

// Betalsätt utan inmatat belopp: frikort/avgiftsfritt är 0 kr, uteblivet får
// klinikens fasta avgift av backend
const UTAN_BELOPP = ['frikort', 'uteblivet', 'avgiftsfri'];

const AMOUNT_PRESETS = [200, 350, 450, 550, 800, 1200];

const PaymentModal = ({ appointment, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    amount: 200,
    payment_method: 'card',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Förslag på pris baserat på bokningstyp
  useEffect(() => {
    if (appointment?.appointment_type?.default_price) {
      setFormData(prev => ({
        ...prev,
        amount: appointment.appointment_type.default_price
      }));
    }
  }, [appointment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (formData.amount <= 0 && !UTAN_BELOPP.includes(formData.payment_method)) {
      setError('Belopp måste vara större än 0');
      return;
    }

    try {
      setLoading(true);
      // Ett virtuellt tillfälle i en serie materialiseras först så att
      // betalningen hamnar på tillfällets egen bokning (Spår R)
      const real = await ensureRealAppointment(appointment);
      await registerPayment({
        appointment_id: real.appointment_id,
        amount: UTAN_BELOPP.includes(formData.payment_method) ? 0 : formData.amount,
        payment_method: formData.payment_method,
        notes: formData.notes
      });
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte registrera betalning');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('sv-SE');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content payment-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Registrera betalning</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="payment-error">{error}</div>}

            {/* Patientinfo */}
            <div className="payment-patient-info">
              <div className="patient-name">
                {appointment.patient?.fullname || 'Ingen patient'}
              </div>
              <div className="appointment-info">
                <span className="apt-type">{appointment.appointment_type?.name || 'Bokning'}</span>
                <span className="apt-time">
                  {formatDate(appointment.start_time)} kl {formatTime(appointment.start_time)}
                </span>
              </div>
            </div>

            {/* Betalningsmetod */}
            <div className="form-group">
              <label>Betalningsmetod</label>
              <div className="payment-method-grid">
                {PAYMENT_METHODS.map(method => (
                  <button
                    key={method.value}
                    type="button"
                    className={`payment-method-btn ${formData.payment_method === method.value ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, payment_method: method.value }))}
                  >
                    <i className={`fas ${method.icon}`}></i>
                    <span>{method.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Belopp */}
            {!UTAN_BELOPP.includes(formData.payment_method) && (
              <div className="form-group">
                <label>Belopp (SEK)</label>
                <div className="amount-input-group">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.amount}
                    onChange={e => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                    className="amount-input"
                  />
                  <span className="amount-suffix">kr</span>
                </div>
                {/* Snabbknappar */}
                <div className="amount-presets">
                  {AMOUNT_PRESETS.map(amount => (
                    <button
                      key={amount}
                      type="button"
                      className={`amount-preset ${formData.amount === amount ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, amount }))}
                    >
                      {amount} kr
                    </button>
                  ))}
                </div>
              </div>
            )}

            {formData.payment_method === 'frikort' && (
              <div className="frikort-info">
                <i className="fas fa-info-circle"></i>
                <span>Frikort registreras utan belopp</span>
              </div>
            )}

            {formData.payment_method === 'uteblivet' && (
              <div className="frikort-info">
                <i className="fas fa-info-circle"></i>
                <span>
                  Klinikens fasta uteblivet-avgift registreras. Bokningen markeras
                  som uteblivet och fakturaunderlag mailas till fakturamottagaren
                  (0 kr och inget underlag om patienten omfattas av klinikens åldersundantag).
                </span>
              </div>
            )}

            {formData.payment_method === 'avgiftsfri' && (
              <div className="frikort-info">
                <i className="fas fa-info-circle"></i>
                <span>
                  Besöket markeras som hanterat utan avgift (t.ex. telefonkontakt).
                  0 kr, inget kvitto.
                </span>
              </div>
            )}

            {/* Anteckning */}
            <div className="form-group">
              <label>Anteckning (valfritt)</label>
              <textarea
                value={formData.notes}
                onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="T.ex. faktureras senare..."
                rows={2}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Avbryt
            </button>
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? 'Registrerar...' : 'Registrera betalning'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;
